import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Wand2, Plus, Trash2 } from 'lucide-react';
import { Dialog } from '@/acc-ui/primitives/Dialog';
import { Input } from '@/acc-ui/primitives/Input';
import { Textarea } from '@/acc-ui/primitives/Textarea';
import { Button } from '@/acc-ui/primitives/Button';
import { mastersApi } from '@/api/other-apis';
import type { MasterRecordDetail } from '@/api/other-apis';

/**
 * MasterRecordEditor — create-or-edit dialog for a master record.
 *
 * The form is **schema-driven**: it reads the master type's JSON Schema
 * (`schemaJson.properties`) and renders one labelled input per field with
 * the right control for its type — text, number, toggle, dropdown (enum),
 * an inline table for arrays-of-objects, and comma lists for primitive
 * arrays. A JSON view is still available as a power-user escape hatch.
 *
 * If `editing` is null, creates a new record; otherwise edits.
 *
 * Validation:
 *   - JSON.parse on submit (errors rendered inline)
 *   - recordKey must be non-empty for creates
 *   - changeReason is required for updates (lightweight audit trail)
 */

// ---------------------------------------------------------------------------
// JSON Schema helpers
// ---------------------------------------------------------------------------

type JsonSchema = {
  type?: string | string[];
  required?: string[];
  properties?: Record<string, JsonSchema>;
  items?: JsonSchema;
  enum?: unknown[];
  description?: string;
  minimum?: number;
  maximum?: number;
};

/** The effective type, ignoring a nullable union like ['string','null']. */
function primaryType(s: JsonSchema | undefined): string {
  if (!s) return 'string';
  const t = s.type;
  if (Array.isArray(t)) return t.find((x) => x !== 'null') ?? 'string';
  return t ?? 'string';
}

/** A blank value appropriate for a freshly-added field of this schema. */
function defaultForSchema(s: JsonSchema | undefined): unknown {
  if (!s) return '';
  if (s.enum) return '';
  switch (primaryType(s)) {
    case 'boolean':
      return false;
    case 'array':
      return [];
    case 'object':
      return {};
    case 'number':
      return undefined; // empty input until the user types
    default:
      return '';
  }
}

/** Infer a minimal schema from a runtime value (for free-form object keys). */
function inferSchema(val: unknown): JsonSchema {
  if (typeof val === 'boolean') return { type: 'boolean' };
  if (typeof val === 'number') return { type: 'number' };
  if (Array.isArray(val)) {
    const first = val.find((v) => v != null);
    return {
      type: 'array',
      items: first && typeof first === 'object' ? { type: 'object' } : { type: typeof first === 'number' ? 'number' : 'string' },
    };
  }
  if (val && typeof val === 'object') return { type: 'object' };
  return { type: 'string' };
}

// ---------------------------------------------------------------------------
// Field labels
// ---------------------------------------------------------------------------

const FIELD_LABELS: Record<string, string> = {
  notes: 'Notes / Description',
  category: 'Category',
  severity: 'Severity',
  maxLoanCap: 'Max Loan Cap (₹)',
  maxLoanCapForCategory: 'Max Loan Cap for Category (₹)',
  productCode: 'Product Code',
  product_code: 'Product Code',
  isSecured: 'Secured Product',
  maxTenureMonths: 'Max Tenure (months)',
  maxFoirPct: 'Max FOIR (%)',
  maxLtvPct: 'Max LTV (%)',
  minAge: 'Min Age (years)',
  maxAge: 'Max Age (years)',
  minCibilScore: 'Min CIBIL Score',
  minScore: 'Minimum CIBIL Score',
  operator: 'Operator',
  loanAmountCaps: 'Loan Amount Caps (Hit / No-Hit)',
  applicableChecklistPoints: 'Applicable Checklist Points',
  minSalary: 'Min Salary (₹)',
  geoRestrictions: 'Geo Restrictions',
  displayName: 'Display Name',
  incomeMethodsAllowed: 'Income Methods Allowed',
  minKycDocsPerApplicant: 'Min KYC Docs / Applicant',
  minApplicantAge: 'Min Applicant Age (years)',
  maxApplicantAge: 'Max Applicant Age (years)',
  validityDaysSanctionToDisbursement: 'Sanction → Disbursement Validity (days)',
  valuationStalenessDays: 'Valuation Staleness (days)',
  ltvCapBands: 'LTV Cap Bands',
  maxAmountInr: 'Max Amount (₹)',
  capPct: 'Cap (%)',
  ruleId: 'Rule ID',
  ucId: 'Use-Case ID',
  enabled: 'Enabled',
  llmEligible: 'LLM Eligible',
  title: 'Title',
  description: 'Description',
  docType: 'Document Type',
  fields: 'Fields',
  segment: 'Product / Segment',
  required_documents: 'Required Documents',
  entries: 'Document Entries',
  mandatory: 'Mandatory',
  requiredFields: 'Required Fields',
  level: 'Level',
  label: 'Label',
  approverIds: 'Approver Employee IDs',
  deviationType: 'Deviation Type',
  requiredLevel: 'Required Level',
  code: 'Code',
  name: 'Name',
  region: 'Region',
  city: 'City',
  state: 'State',
  secret: 'Shared Secret',
  systemMessage: 'System Message',
  userTemplate: 'User Template',
  modelHint: 'Model Hint',
  maxTokens: 'Max Tokens',
  temperature: 'Temperature',
  expectedOutputShape: 'Expected Output Shape',
  rawFieldName: 'Raw Field Name',
  canonicalPath: 'Canonical Path',
  transform: 'Transform',
};

function getFieldLabel(key: string): string {
  return (
    FIELD_LABELS[key] ??
    key
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^./, (s) => s.toUpperCase())
      .trim()
  );
}

const MULTILINE_FIELDS = new Set([
  'systemMessage',
  'userTemplate',
  'notes',
  'description',
  'expectedOutputShape',
]);

function isCurrencyField(key: string): boolean {
  return /cap|amount|salary|inr|loan|ticket/i.test(key);
}

function formatCurrency(v: number): string {
  return `₹${v.toLocaleString('en-IN')}`;
}

// ---------------------------------------------------------------------------
// Array table editor: renders an array-of-objects as an editable table
// ---------------------------------------------------------------------------

type RowData = Record<string, unknown>;

// Fallback column sets for known array fields whose item schema isn't
// fully specified in the master type's JSON Schema.
const ARRAY_FIELD_DEFAULTS: Record<string, RowData> = {
  entries: { docType: '', mandatory: true, severity: 'high', requiredFields: [] },
  required_documents: { docType: '', mandatory: true },
  ltvCapBands: { maxAmountInr: null, capPct: 0 },
  bands: { minAmount: 0, maxAmount: 0, role: '' },
  fields: { name: '', type: 'string', required: true, description: '' },
};

function ArrayTableEditor({
  fieldKey,
  rows,
  onChange,
  itemSchema,
}: {
  fieldKey: string;
  rows: RowData[];
  onChange: (updated: RowData[]) => void;
  itemSchema?: JsonSchema;
}) {
  const colSchemas = itemSchema?.properties ?? {};

  const allKeys = useMemo(() => {
    const keySet = new Set<string>();
    Object.keys(colSchemas).forEach((k) => keySet.add(k));
    rows.forEach((r) => Object.keys(r).forEach((k) => keySet.add(k)));
    if (keySet.size === 0 && ARRAY_FIELD_DEFAULTS[fieldKey]) {
      Object.keys(ARRAY_FIELD_DEFAULTS[fieldKey]).forEach((k) => keySet.add(k));
    }
    return Array.from(keySet);
  }, [rows, fieldKey, colSchemas]);

  const updateCell = (rowIdx: number, col: string, value: unknown) => {
    onChange(rows.map((r, i) => (i === rowIdx ? { ...r, [col]: value } : r)));
  };

  const deleteRow = (rowIdx: number) => {
    onChange(rows.filter((_, i) => i !== rowIdx));
  };

  const addRow = () => {
    let newRow: RowData;
    if (Object.keys(colSchemas).length) {
      newRow = Object.fromEntries(
        Object.entries(colSchemas).map(([k, s]) => [k, defaultForSchema(s)]),
      );
    } else if (ARRAY_FIELD_DEFAULTS[fieldKey]) {
      newRow = { ...ARRAY_FIELD_DEFAULTS[fieldKey] };
    } else {
      newRow = Object.fromEntries(allKeys.map((k) => [k, '']));
    }
    onChange([...rows, newRow]);
  };

  const colLabel = (k: string) => getFieldLabel(k);

  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-x-auto rounded-lg border border-glass-hairline">
        <table className="w-full text-caption border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-glass-hairline">
              {allKeys.map((k) => (
                <th
                  key={k}
                  className="px-3 py-2 text-left font-semibold text-text-secondary whitespace-nowrap"
                >
                  {colLabel(k)}
                </th>
              ))}
              <th className="px-2 py-2 w-8" />
            </tr>
          </thead>
          <tbody className="divide-y divide-glass-hairline">
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={allKeys.length + 1}
                  className="px-3 py-4 text-center text-text-muted text-micro italic"
                >
                  No entries yet — click "Add row" to start.
                </td>
              </tr>
            )}
            {rows.map((row, ri) => (
              <tr key={ri} className="hover:bg-slate-50/60 transition-colors">
                {allKeys.map((col) => (
                  <td key={col} className="px-2 py-1.5 align-middle">
                    <ArrayCell
                      value={row[col]}
                      schema={colSchemas[col]}
                      onChange={(v) => updateCell(ri, col, v)}
                    />
                  </td>
                ))}
                <td className="px-2 py-1.5 align-middle">
                  <button
                    type="button"
                    onClick={() => deleteRow(ri)}
                    className="p-1 rounded text-text-muted hover:text-red-600 hover:bg-red-50 transition-colors"
                    title="Delete row"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        onClick={addRow}
        className="self-start flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-dashed border-brand-blue/40 text-brand-blue text-caption font-medium hover:bg-brand-blue/5 transition-colors"
      >
        <Plus className="w-3.5 h-3.5" />
        Add row
      </button>
    </div>
  );
}

const cellInput =
  'w-full min-w-[110px] rounded border border-glass-hairline bg-white px-2 py-1 text-micro focus:outline-none focus:ring-1 focus:ring-brand-blue/40';

/** Single cell inside an ArrayTableEditor row, typed from the column schema. */
function ArrayCell({
  value,
  schema,
  onChange,
}: {
  value: unknown;
  schema?: JsonSchema;
  onChange: (v: unknown) => void;
}) {
  const effective = schema ?? inferSchema(value);
  const type = primaryType(effective);

  // Enum → dropdown
  if (effective.enum && effective.enum.length) {
    return (
      <select
        className={cellInput}
        value={value == null ? '' : String(value)}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">—</option>
        {effective.enum.map((opt) => (
          <option key={String(opt)} value={String(opt)}>
            {String(opt)}
          </option>
        ))}
      </select>
    );
  }

  if (type === 'boolean') {
    const on = value === true;
    return (
      <button
        type="button"
        onClick={() => onChange(!on)}
        className={`px-2.5 py-0.5 rounded-full text-micro font-semibold transition-colors ${
          on ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
        }`}
      >
        {on ? 'Yes' : 'No'}
      </button>
    );
  }

  if (type === 'array') {
    const arr = Array.isArray(value) ? value : [];
    const itemType = primaryType(effective.items);
    return (
      <input
        type="text"
        className={cellInput}
        value={(arr as unknown[]).join(', ')}
        placeholder="comma-separated"
        onChange={(e) => {
          const parts = e.target.value
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          onChange(itemType === 'number' ? parts.map(Number) : parts);
        }}
      />
    );
  }

  if (type === 'number') {
    return (
      <input
        type="number"
        className={cellInput}
        value={typeof value === 'number' ? value : ''}
        placeholder="0"
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      />
    );
  }

  // string / nullable / fallback
  return (
    <input
      type="text"
      className={cellInput}
      value={value == null ? '' : String(value)}
      placeholder="Enter value…"
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

// ---------------------------------------------------------------------------
// Schema-driven form
// ---------------------------------------------------------------------------

function SchemaForm({
  schema,
  value,
  onChange,
}: {
  schema: JsonSchema;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const required = new Set(schema.required ?? []);

  // Fields = schema properties first (in declared order), then any extra
  // keys present in the value that the schema doesn't describe.
  const fieldKeys = useMemo(() => {
    const keys: string[] = [];
    const seen = new Set<string>();
    for (const k of Object.keys(schema.properties ?? {})) {
      keys.push(k);
      seen.add(k);
    }
    for (const k of Object.keys(value ?? {})) {
      if (!seen.has(k)) keys.push(k);
    }
    return keys;
  }, [schema, value]);

  const setField = (key: string, v: unknown) => {
    onChange({ ...value, [key]: v });
  };

  return (
    <div className="rounded-lg border border-glass-hairline bg-white/80 divide-y divide-glass-hairline">
      {fieldKeys.map((key) => {
        const fieldSchema = schema.properties?.[key] ?? inferSchema(value[key]);
        const type = primaryType(fieldSchema);
        const isArrayOfObjects = type === 'array' && primaryType(fieldSchema.items) === 'object';
        const isFreeObject = type === 'object';
        const wide = isArrayOfObjects || isFreeObject;

        return (
          <div
            key={key}
            className={wide ? 'flex flex-col gap-2 px-4 py-3' : 'flex items-start gap-4 px-4 py-3'}
          >
            <div className={wide ? '' : 'w-[40%] shrink-0 pt-1.5'}>
              <label className="text-caption font-medium text-text-secondary">
                {getFieldLabel(key)}
                {required.has(key) && <span className="text-red-600 ml-0.5">*</span>}
              </label>
              {fieldSchema.description && (
                <p className="text-micro text-text-muted mt-0.5 leading-snug">
                  {fieldSchema.description}
                </p>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <FieldControl
                fieldKey={key}
                schema={fieldSchema}
                value={value[key]}
                onChange={(v) => setField(key, v)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

const fieldInput =
  'w-full rounded-md border border-glass-hairline bg-white px-3 py-1.5 text-caption text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue';

function FieldControl({
  fieldKey,
  schema,
  value,
  onChange,
}: {
  fieldKey: string;
  schema: JsonSchema;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const type = primaryType(schema);

  // Enum → dropdown
  if (schema.enum && schema.enum.length) {
    return (
      <select
        className={fieldInput}
        value={value == null ? '' : String(value)}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <option value="">Select…</option>
        {schema.enum.map((opt) => (
          <option key={String(opt)} value={String(opt)}>
            {String(opt)}
          </option>
        ))}
      </select>
    );
  }

  if (type === 'boolean') {
    const on = value === true;
    return (
      <button
        type="button"
        onClick={() => onChange(!on)}
        className={`px-3 py-1 rounded-full text-micro font-semibold transition-colors ${
          on ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
        }`}
      >
        {on ? 'Yes' : 'No'}
      </button>
    );
  }

  if (type === 'number') {
    const num = typeof value === 'number' ? value : undefined;
    return (
      <div className="flex items-center gap-2">
        <input
          type="number"
          className={fieldInput}
          value={num ?? ''}
          placeholder="0"
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
        />
        {isCurrencyField(fieldKey) && typeof num === 'number' && (
          <span className="text-micro text-text-tertiary whitespace-nowrap">
            {formatCurrency(num)}
          </span>
        )}
      </div>
    );
  }

  if (type === 'array') {
    // Array of objects → inline table
    if (primaryType(schema.items) === 'object') {
      const rows = (Array.isArray(value) ? value : []) as RowData[];
      return (
        <ArrayTableEditor
          fieldKey={fieldKey}
          rows={rows}
          itemSchema={schema.items}
          onChange={(r) => onChange(r)}
        />
      );
    }
    // Checklist points (1..12) → togglable number chips
    if (fieldKey === 'applicableChecklistPoints') {
      return (
        <ChecklistPointsEditor
          value={Array.isArray(value) ? (value as unknown[]).map(Number).filter((n) => !Number.isNaN(n)) : []}
          onChange={(v) => onChange(v)}
        />
      );
    }
    // Array of enum values (e.g. incomeMethodsAllowed) → checkbox chips
    if (schema.items?.enum && schema.items.enum.length) {
      return (
        <CheckboxGroup
          options={schema.items.enum.map((e) => String(e))}
          value={Array.isArray(value) ? (value as unknown[]).map(String) : []}
          onChange={(v) => onChange(v)}
        />
      );
    }
    // Array of primitives → comma-separated
    const arr = Array.isArray(value) ? value : [];
    const itemType = primaryType(schema.items);
    return (
      <div className="flex flex-col gap-1">
        <input
          type="text"
          className={fieldInput}
          value={(arr as unknown[]).join(', ')}
          placeholder="comma-separated"
          onChange={(e) => {
            const parts = e.target.value
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);
            onChange(itemType === 'number' ? parts.map(Number) : parts);
          }}
        />
        {schema.items?.enum && (
          <span className="text-micro text-text-muted">
            Allowed: {schema.items.enum.map((e) => String(e)).join(', ')}
          </span>
        )}
      </div>
    );
  }

  if (type === 'object') {
    // Loan-amount caps → structured category grid (no JSON).
    if (fieldKey === 'loanAmountCaps') {
      return (
        <LoanAmountCapsEditor
          value={value && typeof value === 'object' ? (value as CapsShape) : {}}
          onChange={(v) => onChange(v)}
        />
      );
    }
    // Any other free-form object → compact JSON sub-editor (fallback).
    return <ObjectJsonEditor value={value} onChange={onChange} />;
  }

  // string (incl. nullable string)
  if (MULTILINE_FIELDS.has(fieldKey)) {
    return (
      <Textarea
        rows={4}
        spellCheck={false}
        className="text-caption leading-relaxed"
        value={value == null ? '' : String(value)}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <input
      type="text"
      className={fieldInput}
      value={value == null ? '' : String(value)}
      placeholder="Enter value…"
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** Compact JSON editor for a free-form nested object value. */
function ObjectJsonEditor({
  value,
  onChange,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const [text, setText] = useState(() => JSON.stringify(value ?? {}, null, 2));
  const [err, setErr] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-1">
      <Textarea
        rows={6}
        spellCheck={false}
        className="font-mono text-micro leading-relaxed"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          try {
            onChange(JSON.parse(e.target.value));
            setErr(null);
          } catch (ex) {
            setErr((ex as Error).message);
          }
        }}
      />
      {err ? (
        <span className="text-micro text-red-600">Invalid JSON — {err}</span>
      ) : (
        <span className="text-micro text-text-muted">
          Nested structure — edit as JSON.
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Structured editors (replace raw JSON for known nested product fields)
// ---------------------------------------------------------------------------

function prettyOption(s: string): string {
  return s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

/** Multi-select chips for a fixed enum array (e.g. incomeMethodsAllowed). */
function CheckboxGroup({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const toggle = (opt: string) =>
    onChange(value.includes(opt) ? value.filter((x) => x !== opt) : [...value, opt]);
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const on = value.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            aria-pressed={on}
            onClick={() => toggle(opt)}
            className={`px-2.5 py-1 rounded-full text-micro font-medium border transition-colors ${
              on
                ? 'bg-brand-blue text-white border-brand-blue'
                : 'bg-white text-text-secondary border-glass-hairline hover:bg-slate-50'
            }`}
          >
            {prettyOption(opt)}
          </button>
        );
      })}
    </div>
  );
}

/** Togglable 1..12 chips for applicableChecklistPoints. */
function ChecklistPointsEditor({
  value,
  onChange,
}: {
  value: number[];
  onChange: (v: number[]) => void;
}) {
  const toggle = (p: number) => {
    const next = value.includes(p) ? value.filter((x) => x !== p) : [...value, p];
    next.sort((a, b) => a - b);
    onChange(next);
  };
  return (
    <div className="flex flex-wrap gap-1">
      {Array.from({ length: 12 }, (_, i) => i + 1).map((p) => {
        const on = value.includes(p);
        return (
          <button
            key={p}
            type="button"
            aria-pressed={on}
            onClick={() => toggle(p)}
            className={`w-8 h-8 rounded-md text-micro font-semibold border transition-colors tabular-nums ${
              on
                ? 'bg-brand-blue text-white border-brand-blue'
                : 'bg-white text-text-secondary border-glass-hairline hover:bg-slate-50'
            }`}
          >
            {p}
          </button>
        );
      })}
    </div>
  );
}

// loanAmountCaps shape: { [category]: { hit: {min,max}, noHit: {min,max} } }
type CapCell = { min?: number; max?: number };
type CapRow = { hit?: CapCell; noHit?: CapCell };
type CapsShape = Record<string, CapRow>;

/** Category grid for loanAmountCaps — one row per employer category, with
 *  Hit / No-Hit min-max in ₹. Serializes to the exact stored shape. */
function LoanAmountCapsEditor({
  value,
  onChange,
}: {
  value: CapsShape;
  onChange: (v: CapsShape) => void;
}) {
  const cats = Object.keys(value);
  const setCell = (cat: string, side: 'hit' | 'noHit', field: 'min' | 'max', n: number | undefined) => {
    const row: CapRow = { ...(value[cat] ?? {}) };
    row[side] = { ...(row[side] ?? {}), [field]: n };
    onChange({ ...value, [cat]: row });
  };
  const addCat = () => {
    const next = ['A', 'B', 'C', 'D', 'E', 'F'].find((l) => !(l in value)) ?? `CAT_${cats.length + 1}`;
    onChange({ ...value, [next]: { hit: {}, noHit: {} } });
  };
  const removeCat = (cat: string) => {
    const next = { ...value };
    delete next[cat];
    onChange(next);
  };
  const cell = (cat: string, side: 'hit' | 'noHit', field: 'min' | 'max') => (
    <input
      type="number"
      className={fieldInput}
      value={value[cat]?.[side]?.[field] ?? ''}
      placeholder="—"
      onChange={(e) => setCell(cat, side, field, e.target.value === '' ? undefined : Number(e.target.value))}
    />
  );
  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-x-auto rounded-md border border-glass-hairline">
        <table className="w-full text-micro border-collapse min-w-[34rem]">
          <thead>
            <tr className="text-text-tertiary bg-slate-50/60">
              <th className="text-left px-2 py-1.5 font-semibold">Category</th>
              <th className="px-2 py-1.5 font-semibold">Hit · min ₹</th>
              <th className="px-2 py-1.5 font-semibold">Hit · max ₹</th>
              <th className="px-2 py-1.5 font-semibold">No-Hit · min ₹</th>
              <th className="px-2 py-1.5 font-semibold">No-Hit · max ₹</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {cats.map((cat) => (
              <tr key={cat} className="border-t border-glass-hairline">
                <td className="px-2 py-1 font-semibold text-text-secondary">{cat}</td>
                <td className="px-1 py-1">{cell(cat, 'hit', 'min')}</td>
                <td className="px-1 py-1">{cell(cat, 'hit', 'max')}</td>
                <td className="px-1 py-1">{cell(cat, 'noHit', 'min')}</td>
                <td className="px-1 py-1">{cell(cat, 'noHit', 'max')}</td>
                <td className="px-1 text-center">
                  <button
                    type="button"
                    onClick={() => removeCat(cat)}
                    aria-label={`Remove category ${cat}`}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {cats.length === 0 && (
              <tr>
                <td colSpan={6} className="px-2 py-2 text-text-tertiary">
                  No categories yet — add one below.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        onClick={addCat}
        className="self-start text-micro font-medium text-brand-blue hover:underline"
      >
        + Add category
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form / JSON toggle wrapper
// ---------------------------------------------------------------------------

// Product master types are entered via structured fields only — the raw JSON
// tab is hidden for them (every field has a real control).
const FORM_ONLY_TYPES = new Set(['product_policy', 'product_catalog', 'cibil_thresholds']);

function FormJsonEditor({
  schema,
  contentText,
  setContentText,
  format,
  masterTypeKey,
}: {
  schema: JsonSchema;
  contentText: string;
  setContentText: (v: string) => void;
  format: () => void;
  masterTypeKey: string;
}) {
  const [viewMode, setViewMode] = useState<'form' | 'json'>('form');
  const allowJson = !FORM_ONLY_TYPES.has(masterTypeKey);

  let parsed: Record<string, unknown> | null = null;
  try {
    const p = JSON.parse(contentText);
    if (p && typeof p === 'object' && !Array.isArray(p)) parsed = p;
  } catch {
    // invalid JSON — fall back to JSON view
  }

  const hasSchemaProps = Object.keys(schema.properties ?? {}).length > 0;
  const hasValueKeys = parsed != null && Object.keys(parsed).length > 0;
  const canShowForm = parsed != null && (hasSchemaProps || hasValueKeys);

  const onFormChange = (next: Record<string, unknown>) => {
    setContentText(JSON.stringify(next, null, 2));
  };

  // Form-only types never show JSON (when the form can render). If JSON is
  // invalid and the form can't render, fall back to JSON so it's fixable.
  const effectiveMode = canShowForm ? (allowJson ? viewMode : 'form') : 'json';

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <span className="text-caption font-semibold text-text-secondary">
          {effectiveMode === 'form' ? 'Fields' : 'Content (JSON)'}
        </span>
        <div className="flex items-center gap-1.5">
          {canShowForm && allowJson && (
            <div className="flex rounded-md border border-glass-hairline overflow-hidden text-micro">
              <button
                type="button"
                className={`px-2.5 py-1 transition-colors ${effectiveMode === 'form' ? 'bg-brand-blue text-white font-semibold' : 'bg-white/60 text-text-secondary hover:bg-slate-50'}`}
                onClick={() => setViewMode('form')}
              >
                Form
              </button>
              <button
                type="button"
                className={`px-2.5 py-1 transition-colors ${effectiveMode === 'json' ? 'bg-brand-blue text-white font-semibold' : 'bg-white/60 text-text-secondary hover:bg-slate-50'}`}
                onClick={() => {
                  format();
                  setViewMode('json');
                }}
              >
                JSON
              </button>
            </div>
          )}
          {effectiveMode === 'json' && (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Wand2 className="w-3.5 h-3.5" />}
              onClick={format}
            >
              Format
            </Button>
          )}
        </div>
      </div>

      {effectiveMode === 'form' && parsed ? (
        <SchemaForm schema={schema} value={parsed} onChange={onFormChange} />
      ) : (
        <Textarea
          rows={14}
          spellCheck={false}
          className="font-mono text-caption leading-relaxed"
          value={contentText}
          onChange={(e) => setContentText(e.target.value)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton builder for new records
// ---------------------------------------------------------------------------

/** Build a starter content object from the type's JSON Schema properties. */
function buildSkeleton(schema: JsonSchema): Record<string, unknown> {
  const props = schema.properties ?? {};
  const out: Record<string, unknown> = {};
  for (const [key, propSchema] of Object.entries(props)) {
    const def = defaultForSchema(propSchema);
    if (def !== undefined) out[key] = def;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Editor dialog
// ---------------------------------------------------------------------------

export interface MasterRecordEditorProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  masterTypeKey: string;
  schemaJson: Record<string, unknown>;
  editing?: MasterRecordDetail | null;
  onSaved: () => void;
}

export function MasterRecordEditor({
  open,
  onOpenChange,
  masterTypeKey,
  schemaJson,
  editing,
  onSaved,
}: MasterRecordEditorProps) {
  const qc = useQueryClient();
  const isEdit = Boolean(editing);
  const schema = (schemaJson ?? {}) as JsonSchema;

  const initialContent = useMemo(() => {
    if (editing) return JSON.stringify(editing.content, null, 2);
    const skeleton = buildSkeleton(schema);
    return Object.keys(skeleton).length ? JSON.stringify(skeleton, null, 2) : '{\n  \n}';
  }, [editing, schema]);

  const [recordKey, setRecordKey] = useState(editing?.recordKey ?? '');
  const [contentText, setContentText] = useState(initialContent);
  const [changeReason, setChangeReason] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const reset = () => {
    setRecordKey(editing?.recordKey ?? '');
    setContentText(initialContent);
    setChangeReason('');
    setErr(null);
  };

  const format = () => {
    try {
      const parsed = JSON.parse(contentText);
      setContentText(JSON.stringify(parsed, null, 2));
      setErr(null);
    } catch (e) {
      setErr(`Invalid JSON: ${(e as Error).message}`);
    }
  };

  const mut = useMutation({
    mutationFn: async () => {
      let content: Record<string, unknown>;
      try {
        content = JSON.parse(contentText);
      } catch (e) {
        throw new Error(`Content is not valid JSON: ${(e as Error).message}`);
      }
      if (!isEdit && !recordKey.trim()) {
        throw new Error('Record key is required');
      }
      if (isEdit && !changeReason.trim()) {
        throw new Error('Change reason is required when updating an existing record');
      }

      // Required-field validation against the schema
      const missing = (schema.required ?? []).filter((k) => {
        const v = content[k];
        return v === undefined || v === null || v === '';
      });
      if (missing.length) {
        throw new Error(
          `Missing required field${missing.length > 1 ? 's' : ''}: ${missing
            .map((k) => getFieldLabel(k))
            .join(', ')}`,
        );
      }

      if (isEdit && editing) {
        return mastersApi.updateRecord(editing.id, {
          content,
          changeReason: changeReason.trim(),
        });
      }
      return mastersApi.upsertRecord({
        masterTypeKey,
        recordKey: recordKey.trim(),
        content,
        changeReason: changeReason.trim() || undefined,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['masterType', masterTypeKey] });
      qc.invalidateQueries({ queryKey: ['masterTypes'] });
      if (editing) qc.invalidateQueries({ queryKey: ['masterRecord', editing.id] });
      reset();
      onSaved();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Could not save record'),
  });

  const schemaHint = useMemo(() => {
    if (!schemaJson || Object.keys(schemaJson).length === 0) return null;
    return JSON.stringify(schemaJson, null, 2);
  }, [schemaJson]);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
      title={isEdit ? `Edit record · ${editing?.recordKey}` : `New record in ${masterTypeKey}`}
      description={
        isEdit
          ? 'Saving creates a new version. The previous version is preserved in history and can be rolled back to.'
          : 'Fill in the fields below. Keys should be descriptive and stable (e.g. "retail_home_loan_v1").'
      }
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setErr(null);
              mut.mutate();
            }}
            loading={mut.isPending}
          >
            {isEdit ? 'Save new version' : 'Create record'}
          </Button>
        </>
      }
    >
      {!isEdit && (
        <label className="flex flex-col gap-1">
          <span className="text-caption font-semibold text-text-secondary">
            Record key <span className="text-red-600">*</span>
          </span>
          <Input
            placeholder="retail_home_loan_v1"
            value={recordKey}
            onChange={(e) => setRecordKey(e.target.value)}
          />
        </label>
      )}

      <FormJsonEditor
        schema={schema}
        contentText={contentText}
        setContentText={setContentText}
        format={format}
        masterTypeKey={masterTypeKey}
      />

      {schemaHint && (
        <details className="rounded-glass-sm border border-glass-hairline bg-white/60">
          <summary className="cursor-pointer px-3 py-2 text-caption font-semibold text-text-secondary">
            Schema hint
          </summary>
          <pre className="m-0 px-3 pb-3 pt-0 text-micro text-text-secondary overflow-x-auto leading-relaxed">
            {schemaHint}
          </pre>
        </details>
      )}

      {isEdit && (
        <label className="flex flex-col gap-1">
          <span className="text-caption font-semibold text-text-secondary">
            Change reason <span className="text-red-700">*</span>
          </span>
          <Input
            placeholder="Why is this being changed?"
            value={changeReason}
            onChange={(e) => setChangeReason(e.target.value)}
          />
        </label>
      )}

      {err && (
        <div className="text-caption text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {err}
        </div>
      )}
    </Dialog>
  );
}
